import React, { useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useAuthenticationState } from "../../../store/useAuthenticationState";
import { useUser } from "../../../store/useUser";
import { useUserSecurityState } from "../../../store/useUserSecurityState";
import { ComplianceItem } from "@/components/ui/TrainingListControl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Mail, Lock, Loader, KeyRound, ShieldCheck } from "lucide-react";
import { AD_CLIENT_ID, AD_TENANT_ID, AD_REDIRECT_URI } from "../../../settings";

const LOGIN_PASSWORD = gql`
  mutation LoginPassword($username: String!, $password: String!) {
    loginPassword(username: $username, password: $password){
      state
      token
      extra
    }
  }
`;

const LOGIN_TWOFACTOR = gql`
  mutation LoginTwofactor($token: String!, $code: String!) {
    loginTwofactor(token: $token, code: $code) {
      token
      user {
        username
        realname
        policies
        id
        loginType
        settings {
          colorTheme
          dashboard
          adminSettingsFavorites
          systemSettingsFavorites
          centralPlannerPreferences {
            fullView
            focusColumns
            fullColumns
          }
          summaryFilters
          patientBannerExpanded
          ecmDashboardColumnPreferences {
            page
            visibleColumns
          }
          recentParticipants {
            participantId
            odrNumber
            name
            visitedAt
          }
        }
        compliance {
          id
          name
          item
          type
          description
          pdf
          frequencyDays
          lastCompletionDate
          nextDueDate
          dueInDays
          status
          required
          source
          actionPath
          actionLabel
        }
      }
    }
  }
`;

const REQUEST_AUTHENTICATOR_SUPPORT = gql`
  mutation RequestAuthenticatorSupport($token: String!) {
    requestAuthenticatorSupport(token: $token) {
      success
      message
    }
  }
`;

const GET_LOGIN_NEWS = gql`
  query GetLoginNews {
    getLoginNews {
      enabled
      title
      content
    }
  }
`;



const NewsDisplay = () => {
  const { data: newsData, loading: newsLoading } = useQuery(GET_LOGIN_NEWS);

  if (newsLoading || !newsData?.getLoginNews?.enabled) {
    return null;
  }

  const { title, content } = newsData.getLoginNews;

  return (
    <div className="fixed right-4 top-4 z-[10000] w-[25vw] min-w-[320px] max-w-[420px] rounded-[var(--border-radius-large)] border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] shadow-[var(--shadow-light)]">
      <div className="rounded-t-[var(--border-radius-large)] border-b border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-5 py-4">
        <h3 className="text-sm font-semibold text-[var(--neutral-gray-120)]">{title}</h3>
      </div>
      <div className="px-6 py-5">
        <div
          className="custom-reset text-sm leading-relaxed text-[var(--neutral-gray-100)]"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    </div>
  );
};

// Build stamp to force a fresh compiled bundle on deploy.
void "logincomponent-refresh-2026-04-05";

type FinalAuthStepResult = {
  token?: string;
  user?: any;
};

type LoginSuccessHandler = (token: string, compliance?: ComplianceItem[], user?: any) => void;

const getAuthErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === "object" && "graphQLErrors" in error) {
    const graphQLErrors = (error as { graphQLErrors?: Array<{ message?: string }> }).graphQLErrors;
    const messages = graphQLErrors?.map((graphQLError) => graphQLError?.message).filter(Boolean) || [];
    if (messages.length > 0) {
      return messages.join("\n");
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const MicrosoftIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 23 23" className="h-5 w-5 shrink-0">
    <path fill="#f25022" d="M1 1h10v10H1z" />
    <path fill="#00a4ef" d="M12 1h10v10H12z" />
    <path fill="#7fba00" d="M1 12h10v10H1z" />
    <path fill="#ffb900" d="M12 12h10v10H12z" />
  </svg>
);

export const Login = ({ onSuccess }: { onSuccess?: LoginSuccessHandler }) => {
  const { setAuthenticationStateLogin } = useAuthenticationState();
  const { setUser, setIsUserLoading } = useUser();
  const { setSecurity, setIsSecurityLoading } = useUserSecurityState();
  const [state, setState] = useState({
    username: "",
    password: "",
    error: "",
    twofactor: "",
    state: "login",
    extra : '',
    loading: false,
    token: "",
  });

  const handleAzureLogin = () => {
    setState({ ...state, loading: true });
    // Redirect to Azure AD login
    window.location.href = `https://login.microsoftonline.com/${AD_TENANT_ID}/oauth2/v2.0/authorize?
      client_id=${AD_CLIENT_ID}
      &response_type=code
      &redirect_uri=${encodeURIComponent(AD_REDIRECT_URI)}
      &response_mode=query
      &scope=openid profile email
      &state=${Math.random().toString(36).substring(2, 15)}`;
  };

  const [loginPassword] = useMutation(LOGIN_PASSWORD);
  const [loginTwofactor] = useMutation<{ loginTwofactor: FinalAuthStepResult }>(LOGIN_TWOFACTOR);
  const [requestAuthenticatorSupport, { loading: supportRequestLoading }] = useMutation(REQUEST_AUTHENTICATOR_SUPPORT);
  const [supportRequested, setSupportRequested] = useState(false);
  const [supportRequestError, setSupportRequestError] = useState("");

  const applyAuthResult = (result?: FinalAuthStepResult | null) => {
    if (!result?.token) {
      return null;
    }

    if (result.user) {
      setUser(result.user);
      if (setSecurity) {
        setSecurity(result.user);
      }
    }

    if (setIsUserLoading) {
      setIsUserLoading(false);
    }
    if (setIsSecurityLoading) {
      setIsSecurityLoading(false);
    }

    setAuthenticationStateLogin(result.token);
    return result.token;
  };

  const handleUsername = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    setState({ ...state, loading: true });
    try {
      const loginPasswordResult = await loginPassword({
        variables: {
          username: state.username,
          password: state.password,
        },
      });
      if (loginPasswordResult) {
        const stateMap: { [key: number]: string } = {
          0: "passimage",
          1: "twofactor", 
          2: "activeDirectory",
          3: "totp"
        };
        
        setState({
          ...state,
          state: stateMap[loginPasswordResult.data.loginPassword.state] || "login",
          extra: loginPasswordResult.data.loginPassword.extra,
          token: loginPasswordResult.data.loginPassword.token,
          loading: false,
        });
      }
    } catch (error) {
      setState({ 
        ...state, 
        error: getAuthErrorMessage(error, "Invalid credentials"), 
        loading: false,
        state: "login" 
      });
    }
  };

  const handleTwofactor = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    setState({ ...state, loading: true });
    try {
      const loginTwofactorResult = await loginTwofactor({
        variables: {
          token: state.token,
          code: state.twofactor,
        },
      });
      const authResult = loginTwofactorResult?.data?.loginTwofactor;
      const token = applyAuthResult(authResult);
      if (!token) {
        throw new Error('Missing login token');
      }
      if (onSuccess) {
        onSuccess(token, authResult?.user?.compliance, authResult?.user);
      }
    } catch (error) {
      setState({ 
        ...state, 
        error: getAuthErrorMessage(error, "Invalid code"), 
        loading: false,
        state: "login"
      });
    }
  };

  const handleTOTP = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    setState({ ...state, loading: true });
    try {
      const loginTwofactorResult = await loginTwofactor({
        variables: {
          token: state.token,
          code: state.twofactor,
        },
      });
      const authResult = loginTwofactorResult?.data?.loginTwofactor;
      const token = applyAuthResult(authResult);
      if (!token) {
        throw new Error('Missing login token');
      }
      if (onSuccess) {
        onSuccess(token, authResult?.user?.compliance, authResult?.user);
      }
    } catch (error) {
      setState({ 
        ...state, 
        error: getAuthErrorMessage(error, "Invalid authentication code"), 
        loading: false
      });
    }
  };

  const handleAuthenticatorSupport = async () => {
    setSupportRequestError("");
    try {
      await requestAuthenticatorSupport({ variables: { token: state.token } });
      setSupportRequested(true);
    } catch (_requestError) {
      setSupportRequestError("Unable to submit your request. Please try again.");
    }
  };

  const handleReturnToLogin = () => {
    setSupportRequested(false);
    setSupportRequestError("");
    setState({
      ...state,
      error: "",
      twofactor: "",
      state: "login",
      extra: "",
      loading: false,
      token: "",
    });
  };

  const fieldShellClassName = "flex h-10 items-center rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-50)] bg-[var(--surface-panel-solid)] transition focus-within:border-[var(--azure-primary)] focus-within:ring-2 focus-within:ring-[var(--azure-focus-ring)]";
  const fieldInputClassName = "h-10 min-w-0 flex-1 w-full border-0 bg-transparent px-0 pr-3 text-sm text-[var(--neutral-gray-120)] placeholder:text-[var(--neutral-gray-80)] shadow-none focus-visible:border-transparent focus-visible:ring-0";
  const primaryButtonClassName = "h-11 w-full text-base";
  const secondaryButtonClassName = "h-10 w-full border-[var(--azure-primary)] bg-[var(--azure-primary)] text-white hover:border-[var(--azure-primary-hover)] hover:bg-[var(--azure-primary-hover)] hover:text-white";

  return (
    <>
      <NewsDisplay />
      <div className="w-full max-w-[400px] overflow-hidden rounded-[var(--border-radius-large)] border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] shadow-[var(--shadow-light)]">
        <div className="flex items-center justify-between border-b border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-6 py-3">
          <a href="https://dhs.lacounty.gov/" target="_blank" rel="noreferrer">
            <img
              src={`${process.env.PUBLIC_URL}/dhs-logo.svg`}
              alt="Los Angeles County Department of Health Services"
              className="h-7 w-auto"
            />
          </a>
          <a href="https://lacounty.gov/" target="_blank" rel="noreferrer" className="opacity-70 transition hover:opacity-100">
            <img
              src={`${process.env.PUBLIC_URL}/lacountyseal.png`}
              alt="County of Los Angeles seal"
              className="h-7 w-7 object-contain"
            />
          </a>
        </div>

        <div className="space-y-6 px-6 py-6">
          <div className="space-y-3 text-center">
            <img
              src={`${process.env.PUBLIC_URL}/logoODR.png`}
              alt="Office of Diversion and Reentry"
              className="mx-auto h-16 w-auto"
            />
            <div className="text-2xl font-bold tracking-tight text-[var(--neutral-gray-120)]">
              Diversion Database
            </div>
          </div>

          {/* Login Form */}
          {state.state === "login" && (
            <div className="space-y-5">
              <form onSubmit={handleUsername} className="space-y-5">
                <div className="space-y-4">
                  {/* Username Field */}
                  <div className="space-y-2">
                    <label htmlFor="login-username" className="block text-xs font-medium text-[var(--neutral-gray-100)]">
                      Username
                    </label>
                    <div className={fieldShellClassName}>
                      <Mail className="mx-3 h-4 w-4 text-[var(--neutral-gray-80)]" />
                      <Input
                        id="login-username"
                        autoComplete="username"
                        type="text"
                        value={state.username}
                        onChange={(e) =>
                          setState({ ...state, username: e.target.value })
                        }
                        className={fieldInputClassName}
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="space-y-2">
                    <label htmlFor="login-password" className="block text-xs font-medium text-[var(--neutral-gray-100)]">
                      Password
                    </label>
                    <div className={fieldShellClassName}>
                      <Lock className="mx-3 h-4 w-4 text-[var(--neutral-gray-80)]" />
                      <Input
                        id="login-password"
                        autoComplete="current-password"
                        type="password"
                        value={state.password}
                        onChange={(e) =>
                          setState({ ...state, password: e.target.value })
                        }
                        className={fieldInputClassName}
                      />
                    </div>
                  </div>
                </div>

                {state.error && (
                  <div className="text-center text-xs text-[var(--semantic-error)]">
                    {state.error}
                  </div>
                )}

                <Button
                  type="submit"
                  variant="outline"
                  disabled={state.loading}
                  className={secondaryButtonClassName}
                >
                  {state.loading ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <KeyRound className="h-4 w-4 text-[#ffd166]" strokeWidth={2.75} />
                      <span>Login with Email/Password</span>
                    </>
                  )}
                </Button>
              </form>

              <div className="text-center">
                <div className="text-xs text-[var(--neutral-gray-90)]">
                  <a
                    href="/support/lostpassword"
                    className="text-[var(--azure-primary)] hover:text-[var(--azure-primary-hover)]"
                  >
                    Forgot your password?
                  </a>
                </div>
              </div>

              <div className="space-y-4 border-t border-[var(--neutral-gray-30)] pt-5">
                <div className="text-center text-sm font-bold text-[var(--neutral-gray-110)]">
                  For Los Angeles County Employees
                </div>
                <Button
                  type="button"
                  onClick={handleAzureLogin}
                  className={primaryButtonClassName}
                >
                  <MicrosoftIcon />
                  <span>Sign in with LA County SSO</span>
                </Button>
              </div>
            </div>
          )}

          {/* Pass Image Selection */}
          {state.state === "passimage" && (
            <div className="space-y-6">
              <div className="text-center text-sm text-[var(--neutral-gray-100)]">
                Please select your security image
              </div>
              <div className="grid grid-cols-3 gap-6">
                {[...state.extra.split(',')].map((num) => (
                  <Button
                    key={num}
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      setState({ ...state, loading: true });
                      try {
                        const loginTwofactorResult = await loginTwofactor({
                          variables: {
                            token: state.token,
                            code: num,
                          },
                        });
                        const authResult = loginTwofactorResult?.data?.loginTwofactor;
                        const token = applyAuthResult(authResult);
                        if (!token) {
                          throw new Error('Missing login token');
                        }
                        if (onSuccess) {
                          onSuccess(token, authResult?.user?.compliance, authResult?.user);
                        }
                      } catch (error) {
                        setState({ 
                          ...state, 
                          error: "Invalid image selected", 
                          loading: false,
                          state: "login" 
                        });
                      }
                    }}
                    className="h-auto rounded-[var(--border-radius-medium)] p-3 hover:border-[var(--azure-primary)]"
                  >
                    <img 
                      src={`/ui/build/passimages/${num}.png`}
                      alt={`Security option ${num}`}
                      className="aspect-square w-full scale-150 rounded-[var(--border-radius-small)] border border-[var(--neutral-gray-30)] object-cover"
                    />
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Two Factor Form */}
          {state.state === "twofactor" && (
            <form onSubmit={handleTwofactor} className="mt-8 space-y-6">
              <div className="text-center text-sm text-[var(--neutral-gray-100)]">
                A code has been sent to your email address
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-[var(--neutral-gray-110)]">
                  Two Factor Code
                </label>
                <Input
                  type="text"
                  value={state.twofactor}
                  onChange={(e) =>
                    setState({ ...state, twofactor: e.target.value })
                  }
                  className="w-full"
                />
              </div>

              {state.error && (
                <div className="text-center text-sm text-[var(--semantic-error)]">
                  {state.error}
                </div>
              )}

              <Button
                type="submit"
                disabled={state.loading}
                className={primaryButtonClassName}
              >
                {state.loading ? (
                  <Loader className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <ShieldCheck className="h-4 w-4" />
                    <span>Verify</span>
                  </>
                )}
              </Button>

            </form>
          )}

          {/* TOTP Form */}
          {state.state === "totp" && supportRequested && (
            <div className="mt-8 space-y-5">
              <div
                role="status"
                className="rounded-[var(--border-radius-medium)] border border-[var(--semantic-success)] bg-[var(--surface-muted)] p-4 text-center text-sm text-[var(--neutral-gray-110)]"
              >
                If the account is active, Support will contact you.
              </div>
              <Button
                id="login-authenticator-support-back"
                type="button"
                variant="link"
                onClick={handleReturnToLogin}
                className="h-auto w-full p-0 text-sm"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to login
              </Button>
            </div>
          )}

          {state.state === "totp" && !supportRequested && (
            <form onSubmit={handleTOTP} className="mt-8 space-y-6">
              <div className="text-center text-sm text-[var(--neutral-gray-100)]">
                Enter the 6-digit code from your authenticator app
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-[var(--neutral-gray-110)]">
                  Authentication Code
                </label>
                <Input
                  type="text"
                  value={state.twofactor}
                  onChange={(e) =>
                    setState({ ...state, twofactor: e.target.value })
                  }
                  placeholder="000000"
                  maxLength={6}
                  className="w-full text-center text-lg font-mono"
                />
                <div className="mt-2 text-center text-xs text-[var(--neutral-gray-90)]">
                  Or enter a backup code if you don't have access to your authenticator
                </div>
              </div>

              {state.error && (
                <div className="text-center text-sm text-[var(--semantic-error)]">
                  {state.error}
                </div>
              )}

              <Button
                type="submit"
                disabled={state.loading}
                className={primaryButtonClassName}
              >
                {state.loading ? (
                  <Loader className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <ShieldCheck className="h-4 w-4" />
                    <span>Verify</span>
                  </>
                )}
              </Button>

              <Button
                id="login-authenticator-support"
                type="button"
                variant="link"
                onClick={handleAuthenticatorSupport}
                disabled={supportRequestLoading || !state.token}
                className="h-auto w-full p-0 text-sm"
              >
                {supportRequestLoading ? "Contacting Support..." : "Can't access authenticator app?"}
              </Button>
              {supportRequestError && (
                <div className="text-center text-sm text-[var(--semantic-error)]">
                  {supportRequestError}
                </div>
              )}
            </form>
          )}

          <div className="border-t border-[var(--neutral-gray-30)] pt-4 text-center text-xs leading-4 text-[var(--neutral-gray-90)]">
            <div>Los Angeles County Department of Health Services</div>
            <div>Office of Diversion and Reentry</div>
          </div>
        </div>
      </div>
    </>
  );
};
